import { DashboardDatedSalesStatisticsState } from "../../store/dashboard/sales/types";
import { DashboardUserEventsStatisticsState } from "../../store/dashboard/userevents/types";
import { Refund } from "../../models/Statistics";
import { statisticsUserEventsDefault } from "./components/UserEvents/types";
import { UserEvent } from "./types";

export const statisticsKpiDefault = {
  sales: {
    isLoading: true,
    purchases: {
      now: 0,
      then: 1
    },
    totalSales: {
      now: 0,
      then: 1
    }
  },
  userEvents: {
    isLoading: true,
    conversionRate: {
      now: 0,
      then: 1
    },
    visitors: {
      now: 0,
      then: 1
    }
  },
  refunds: {
    isLoading: true,
    now: 0,
    then: 1
  }
};

export function updateUserEventsKpiStatistics(
  userEventsStatisticsState: DashboardUserEventsStatisticsState
) {
  const { userEvents, isLoading } = userEventsStatisticsState;
  if (!userEvents.length) {
    return { ...statisticsKpiDefault.userEvents, isLoading: isLoading };
  }

  return {
    isLoading: isLoading,
    visitors: {
      then: userEvents ? getNumberOfVisits(userEvents) : 1
    },
    conversionRate: {
      then: userEvents ? getConversionRate(userEvents) : 1
    }
  };
}

export const updateUserEventsStatistics = (
  userEventsStatisticsState: DashboardUserEventsStatisticsState
) => {
  const { userEvents, isLoading } = userEventsStatisticsState;
  if (!userEvents.length) {
    return { ...statisticsUserEventsDefault, isLoading: isLoading };
  }

  const events = userEvents[0];
  let unique = new Map<string, UserEvent>();

  for (let i = 0; i < events.length; i++) {
    const userId = events[i].user.id;
    const addedEvent = unique.get(userId);

    if (!addedEvent) {
      unique.set(userId, events[i]);
    } else {
      if (
        addedEvent.event !== events[i].event &&
        addedEvent.event === "OpenDoor"
      ) {
        unique.set(userId, events[i]);
      }
    }
  }
  const uniqueEvents = Array.from(unique.values());
  sortUserEventsByTimestamp(uniqueEvents);

  return {
    isLoading: isLoading,
    events: uniqueEvents.length ? uniqueEvents.slice(0, 10) : []
  };
};

const sortUserEventsByTimestamp = (userEvents: UserEvent[]) =>
  userEvents.sort(
    (a: UserEvent, b: UserEvent) =>
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

const getNumberOfVisits = (events: any[]) =>
  events.filter(event => event.event === "OpenDoor").length;

const getConversionRate = (events: any[]) => {
  const numberOfVisits = getNumberOfVisits(events);
  if (!numberOfVisits) {
    return 0;
  }
  const numberOfPayments = events.filter(
    event => event.type === "Payment"
  ).length;
  return Math.round((numberOfPayments / numberOfVisits) * 100);
};

export const updateDatedSalesStatistics = (
  datedSalesStatisticsState: DashboardDatedSalesStatisticsState
) => {
  const { datedSalesStatistics, isLoading } = datedSalesStatisticsState;
  if (!datedSalesStatistics.length) {
    return { ...statisticsKpiDefault.sales, isLoading: isLoading };
  }

  return {
    isLoading: isLoading,
    purchases: {
      then: datedSalesStatistics
        ? getNumberOfPurchases(datedSalesStatistics)
        : 1
    },
    totalSales: {
      then: datedSalesStatistics ? getTotalSales(datedSalesStatistics) : 1
    }
  };
};

const getNumberOfPurchases = (stats: any[]) =>
  stats
    .reduce((acc, stat) => {
      return [...acc, Object.values(stat.sumReceiptByDateTime)];
    }, [])
    .flat().length;

const getTotalSales = (stats: any[]) =>
  stats
    .reduce((acc, stat) => {
      return [...acc, Object.values(stat.sumReceiptByDateTime)];
    }, [])
    .flat()
    .reduce((a: number, b: any) => a + b.sum, 0);

export const updateRefundsStatistics = (refunds: Refund[][]) => {
  if (!refunds.length) {
    return statisticsKpiDefault.refunds;
  }

  return {
    isLoading: false,
    now: refunds[0].reduce((a: number, b) => a + b.quantity, 0) || 0,
    then: refunds[1].reduce((a: number, b) => a + b.quantity, 0) || 1
  };
};

export const calculateTrend = (now: number, then: number) => {
  const trend = Math.round((now / (then || 1)) * 100);
  return trend !== 0 ? trend - 100 : trend;
};
